"use client"

import { MessageBlockContent, Block, Theme, defaultTheme, getPlainText } from "@/types"
import { useState, useEffect, useMemo, useRef } from "react"
import { BlockImage } from "../BlockImage"
import { RichTextRenderer } from "../RichTextRenderer"

interface MessageBlockProps {
  content: MessageBlockContent
  onAnswer?: (answer: any) => void
  answer?: any
  allAnswers?: Record<string, any>
  allBlocks?: Block[]
  onAutoAdvance?: () => void
  theme?: Theme | null
  previewMode?: "desktop" | "mobile"
}

export function MessageBlock({ content, onAnswer, allAnswers = {}, allBlocks = [], onAutoAdvance, theme = null }: MessageBlockProps) {
  // Use provided theme or fallback to default
  const activeTheme = theme || defaultTheme
  const [progress, setProgress] = useState<number>(0)
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false)
  
  // Store callbacks in refs to avoid effect re-runs when they change
  const onAnswerRef = useRef(onAnswer)
  const onAutoAdvanceRef = useRef(onAutoAdvance)
  
  // Update refs when callbacks change
  useEffect(() => {
    onAnswerRef.current = onAnswer
  }, [onAnswer])
  
  useEffect(() => {
    onAutoAdvanceRef.current = onAutoAdvance
  }, [onAutoAdvance])

  // Simplified animation for rich text - just fade in
  useEffect(() => {
    const displayDelay = content.typingAnimation ? 500 : 100
    
    // First, mark as completed after display animation
    const displayTimer = setTimeout(() => {
      onAnswerRef.current?.("completed")
      
      // If auto-advance is enabled, start the countdown
      if (content.autoAdvance && onAutoAdvanceRef.current) {
        const delay = content.autoAdvanceDelay || 2000
        setIsAutoAdvancing(true)
        setProgress(0)
        
        // Update progress smoothly (every 50ms for smooth animation)
        const startTime = Date.now()
        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime
          const newProgress = Math.min((elapsed / delay) * 100, 100)
          setProgress(newProgress)
          
          if (newProgress >= 100) {
            clearInterval(progressInterval)
          }
        }, 50)
        
        // Auto-advance after delay
        const advanceTimer = setTimeout(() => {
          setIsAutoAdvancing(false)
          onAutoAdvanceRef.current?.()
        }, delay)
        
        return () => {
          clearTimeout(advanceTimer)
          clearInterval(progressInterval)
          setIsAutoAdvancing(false)
          setProgress(0)
        }
      }
    }, displayDelay)
    
    return () => clearTimeout(displayTimer)
  }, [content.text, content.typingAnimation, content.autoAdvance, content.autoAdvanceDelay])

  return (
    <div className="max-w-3xl mx-auto">
      <div className="prose prose-lg max-w-none text-center">
        <div 
          className={`text-xl leading-relaxed ${content.typingAnimation ? 'animate-fade-in' : ''}`}
          style={{ 
            color: activeTheme.colors.text,
            fontFamily: activeTheme.fonts.body
          }}
        >
          <RichTextRenderer content={content.text} />
        </div>
        
        {/* Auto-advance progress indicator - always rendered to prevent layout shift */}
        {content.autoAdvance && (
          <div 
            className={`mt-6 flex justify-center transition-opacity duration-300 ${
              isAutoAdvancing ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-12 h-12 transform -rotate-90">
                {/* Background circle */}
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  className="text-gray-200"
                />
                {/* Progress circle */}
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke={activeTheme.colors.primary}
                  strokeWidth="3"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress / 100)}`}
                  className="transition-all duration-75 ease-linear"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
